
import React from 'react'
import { getHealthOverviewCached, toSymbolMap } from '@/modules/crypto-assets/services/healthCache'
import { useToast } from '@/shared/ui/Toast'
import { Button, Modal, DropdownMenu, CircularProgress } from '@/shared/ui'
import { MoreHorizontal, CheckCircle, AlertTriangle } from 'lucide-react'

import { ManageHeader } from './ManageHeader'
import { AssetsInfiniteGrid } from './AssetsInfiniteGrid'
import AddBybitPanel, { type Option as BybitOption } from './AddBybitPanel'
import HealthOverviewModal from './HealthOverviewModal'
import { BootProgressModal } from './BootProgressModal'
import { EditAssetNameModal } from './EditAssetNameModal'

import { uploadLogoAsset } from '@/modules/upload/services/uploadService'
import {
  listCryptoAssetsPaged,
  insertCryptoAsset,
  updateCryptoLogo,
  type CryptoAsset,
  safeDeleteAsset,
  timeframeHealthSymbol,
  type TimeframeHealth,
  timeframeHealthOverview,
  type TimeframeHealthOverview,
  currentBucketFrom5m,
  type CurrentBucket,
  activateAssetSeed,
  deactivateAssetCleanup,
} from '@/modules/crypto-assets/services/cryptoAssetsService'
import { runQuickBootstrap, runBootstrapForTimeframe, targetForTimeframe } from '@/modules/crypto-assets/services/bootstrapService'
import { fetchBybitSpotSymbolsDetailed } from '@/modules/market/services/bybitService'

type Option = BybitOption

export default function ManageCryptoAssets() {
  const { add: toast } = useToast()

  // Lista (paginada)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [assets, setAssets] = React.useState<CryptoAsset[]>([])
  const [page, setPage] = React.useState(0)
  const [pageSize] = React.useState(25)
  const [total, setTotal] = React.useState(0)
  const [paging, setPaging] = React.useState(false)

  // Bybit (lista local) + filtros
  const [bybit, setBybit] = React.useState<Option[]>([])
  const [filter, setFilter] = React.useState('')
  const [sort, setSort] = React.useState<'symbol-asc'|'symbol-desc'|'name-asc'|'name-desc'>('symbol-asc')
  const [batchMode, setBatchMode] = React.useState(false)
  const [addingOne, setAddingOne] = React.useState<string | null>(null)

  // Remoção
  const [delOpen, setDelOpen] = React.useState(false)
  const [delAsset, setDelAsset] = React.useState<CryptoAsset | null>(null)
  const [delConfirm, setDelConfirm] = React.useState('')
  const [deleting, setDeleting] = React.useState(false)

  // Saúde
  const [healthOpen, setHealthOpen] = React.useState(false)
  const [healthAsset, setHealthAsset] = React.useState<CryptoAsset | null>(null)
  const [health, setHealth] = React.useState<TimeframeHealth | null>(null)
  const [healthLoading, setHealthLoading] = React.useState(false)
  const [currentBuckets, setCurrentBuckets] = React.useState<Record<string, CurrentBucket | null>>({})
  const [tfProgress, setTfProgress] = React.useState<Record<string, number>>({})

  // Visão geral
  const [ovOpen, setOvOpen] = React.useState(false)
  const [ovLoading, setOvLoading] = React.useState(false)
  const [ovData, setOvData] = React.useState<TimeframeHealthOverview | null>(null)
  const [overviewMap, setOverviewMap] = React.useState<Record<string, TimeframeHealth | undefined>>({})
  const [healthFilter, setHealthFilter] = React.useState<'ALL'|'OK'|'WARN'>('ALL')
  const [logoFilter, setLogoFilter] = React.useState<'ALL'|'WITH'|'WITHOUT'>('ALL')
  const [activeFilter, setActiveFilter] = React.useState<'ALL'|'ACTIVE'|'INACTIVE'>('ALL')
  const [quoteFilter, setQuoteFilter] = React.useState<'ALL'|'USDT'|'USDC'|'BTC'>('ALL')
  const [addOpen, setAddOpen] = React.useState(false)

  // Editar nome
  const [editOpen, setEditOpen] = React.useState(false)
  const [editAsset, setEditAsset] = React.useState<CryptoAsset | null>(null)

  // Boot geral
  const [bootOpen, setBootOpen] = React.useState(false)
  const [bootPct, setBootPct] = React.useState(0)
  const [bootingSymbol, setBootingSymbol] = React.useState<string | null>(null)
  const [bootTfs, setBootTfs] = React.useState<Array<{ tf: string; status: 'pending'|'running'|'done' }>>([])

  // Confirmar ativar/inativar
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [confirmMode, setConfirmMode] = React.useState<'activate'|'deactivate'|null>(null)
  const [confirmAsset, setConfirmAsset] = React.useState<CryptoAsset | null>(null)
  const [confirmLoading, setConfirmLoading] = React.useState(false)

  // Carga inicial + Bybit
  async function loadAll(reset: boolean = true) {
    if (reset) { setLoading(true); setPage(0); setTotal(0); setAssets([]) }
    setError(null)
    try {
      const [pageRes, list] = await Promise.all([
        listCryptoAssetsPaged({ page: reset ? 0 : page, pageSize }),
        fetchBybitSpotSymbolsDetailed(),
      ])
      if (reset) {
        setAssets(pageRes.items)
        setTotal(pageRes.total || 0)
        setPage(0)
      } else {
        setAssets(prev => [...prev, ...pageRes.items])
        setTotal(pageRes.total || 0)
      }
      const options: Option[] = list
        .filter((i) => (i.status || '').toLowerCase() !== 'offline')
        .map((i) => ({ symbol: String(i.symbol).toUpperCase(), base: i.baseCoin || '', quote: i.quoteCoin || '', name: i.name }))
        .filter((o) => (o.quote || '').toUpperCase() === 'USDT')
      setBybit(options)
    } catch (e:any) {
      setError(e?.message || 'Falha ao carregar')
    } finally { setLoading(false) }
  }

  async function loadMore() {
    if (paging) return
    setPaging(true)
    try {
      const { items } = await listCryptoAssetsPaged({ page: page+1, pageSize })
      if (!items.length) { setPage(p => p+1); return }
      setAssets(prev => [...prev, ...items])
      setPage(p => p+1)
    } catch (e:any) { setError(e?.message || 'Falha ao paginar') } finally { setPaging(false) }
  }

  React.useEffect(()=>{ loadAll(true) }, [])

  async function refreshOverview(force = false) {
    try { const ov = await getHealthOverviewCached(force); setOverviewMap(toSymbolMap(ov)) } catch {}
  }
  // cache de saúde por símbolo
  
  React.useEffect(()=>{ refreshOverview(false) }, [assets.length])
  // Mantém a lista sincronizada com o backend: refresh forçado a cada 60s
  React.useEffect(() => {
    const id = window.setInterval(() => { refreshOverview(true).catch(()=>{}) }, 60000)
    return () => { window.clearInterval(id) }
  }, [])

  // Ações
  async function onPickLogo(a: CryptoAsset, f?: File) {
    try {
      if (!f) return
      const up = await uploadLogoAsset(a.symbol, f)
      await updateCryptoLogo(a.id, up.url)
      setAssets(prev => prev.map(x => x.id === a.id ? { ...x, logo_url: up.url } : x))
      toast({ title: 'Logo', description: 'Atualizado com sucesso', variant: 'success' })
    } catch (e:any) { toast({ title: 'Logo', description: e?.message || 'Falha ao enviar logo', variant: 'error' }) }
  }

  async function onAddOne(o: Option) {
    setAddingOne(o.symbol)
    try {
      const res = await insertCryptoAsset({ symbol: o.symbol, base_asset: o.base, quote_asset: o.quote, name: o.name || undefined })
      const added: CryptoAsset = { id: res.id, symbol: o.symbol, base_asset: o.base, quote_asset: o.quote, name: o.name || null, logo_url: null, is_active: false }
      setAssets(prev => [...prev, added].sort((a,b)=> a.symbol.localeCompare(b.symbol)))
      toast({ title: 'Ativo adicionado', description: `${o.symbol} cadastrado.`, variant: 'success', soft: true })
    } catch (e:any) { toast({ title: 'Adicionar', description: e?.message || 'Falha ao cadastrar ativo', variant: 'error' }) }
    finally { setAddingOne(null) }
  }

  async function onOpenHealth(asset: CryptoAsset) {
    setError(null); setHealthAsset(asset); setHealthOpen(true); setHealth(null); setHealthLoading(true); setCurrentBuckets({})
    try {
      const h = await timeframeHealthSymbol(asset.symbol); setHealth(h); setOverviewMap(prev => ({ ...prev, [asset.symbol]: h as any }))
      const tfs = h.timeframes.map(t=> t.timeframe)
      const entries = await Promise.all(tfs.map(async tf => { try { const cur = await currentBucketFrom5m(asset.symbol, tf); return [tf, cur] as const } catch { return [tf, null] as const } }))
      const map: Record<string, CurrentBucket | null> = {}; for (const [tf, cur] of entries) map[tf] = cur; setCurrentBuckets(map)
    } catch (e:any) { setError(e?.message || 'Falha ao carregar Saúde de timeframes') } finally { setHealthLoading(false) }
  }

  async function onForceTf(asset: CryptoAsset, tf: string) {
    try {
      setTfProgress(prev => ({ ...prev, [tf]: 5 }))
      const { afterCount } = await runBootstrapForTimeframe(asset.symbol, tf as any, { cap: 150000, onProgress: (p) => setTfProgress(prev => ({ ...prev, [tf]: p })) })
      await onOpenHealth(asset)
      setTfProgress(prev => { const { [tf]: _, ...rest } = prev; return rest })
      const target = tf === 'W' ? 500 : targetForTimeframe(tf)
      const msg = afterCount >= target ? 'Janela alvo completa' : `Coletados ${afterCount} de ${target} (máximo disponível)`
      toast({ title: 'Forçar janela', description: `${asset.symbol} - TF ${tf}: ${msg}`, variant: afterCount >= target ? 'success' : 'warning', soft: true })
    } catch (e: any) {
      setTfProgress(prev => { const { [tf]: _, ...rest } = prev; return rest })
      setError(e?.message || 'Falha ao forçar TF')
      toast({ title: 'Falha', description: e?.message || 'Não foi possível forçar janela para o TF', variant: 'error', soft: true })
    }
  }

  async function onForceAllTfs() {
    if (!healthAsset) return
    try {
      setBootOpen(true); setBootPct(0); setBootingSymbol(healthAsset.symbol)
      const tfs = ['60','240','720','D','W','M','30','15','5']
      setBootTfs(tfs.map(tf => ({ tf, status: 'pending' as const })))
      await runQuickBootstrap(healthAsset.symbol, {
        target: 500,
        backBuckets: 300,
        onStart: (_tf, idx) => { setBootTfs(prev => prev.map((t, i) => i === (idx - 1) ? { ...t, status: 'running' } : t)) },
        onStep: (_tf, idx) => { setBootTfs(prev => prev.map((t, i) => i === (idx - 1) ? { ...t, status: 'done' } : t)) },
        onProgress: (p) => setBootPct(p),
      })
      await onOpenHealth(healthAsset)
      toast({ title: 'Forçar janela (9 TFs)', description: `${healthAsset.symbol}: preenchimento concluído (ou máximo disponível)`, variant: 'success', soft: true })
    } catch (e: any) { setBootOpen(false); toast({ title: 'Falha', description: e?.message || 'Não foi possível forçar janela nos 9 TFs', variant: 'error', soft: true }) }
  }

  async function onConfirmAction() {
    if (!confirmAsset || !confirmMode) return
    setConfirmLoading(true)
    try {
      if (confirmMode === 'activate') {
        await activateAssetSeed(confirmAsset.symbol)
        toast({ title: 'Ativar', description: `${confirmAsset.symbol} ativado; coleta iniciada.`, variant: 'success' })
      } else {
        await deactivateAssetCleanup(confirmAsset.symbol)
        toast({ title: 'Inativar', description: `${confirmAsset.symbol} inativado; fila cancelada.`, variant: 'info' })
      }
      setConfirmOpen(false)
      await loadAll(true)
    } catch (e:any) { toast({ title: 'Falha', description: e?.message || 'Erro na ação', variant: 'error' }) }
    finally { setConfirmLoading(false) }
  }

  async function onSaveEditName(name: string) {
    if (!editAsset) return
    setAssets(prev => prev.map(x => x.id === editAsset.id ? { ...x, name } : x))
    setEditOpen(false)
    toast({ title: 'Nome', description: 'Atualizado com sucesso', variant: 'success' })
  }

  const displayed: CryptoAsset[] = React.useMemo(() => {
    let arr = assets
    const q = filter.trim().toUpperCase()
    if (q) {
      arr = arr.filter(
        (a) => a.symbol.includes(q) || (a.name || '').toUpperCase().includes(q),
      )
    }
    if (healthFilter !== 'ALL') {
      arr = arr.filter((a) => {
        const h = overviewMap[a.symbol]
        const warn = !!h && (h.warnings || 0) > 0
        return healthFilter === 'OK' ? !warn : warn
      })
    }
    if (logoFilter !== 'ALL') {
      arr = arr.filter((a) => (logoFilter === 'WITH' ? !!a.logo_url : !a.logo_url))
    }
    if (activeFilter !== 'ALL') {
      arr = arr.filter((a) => (activeFilter === 'ACTIVE' ? !!a.is_active : !a.is_active))
    }
    if (quoteFilter !== 'ALL') {
      arr = arr.filter((a) => (a.quote_asset || '').toUpperCase() === quoteFilter)
    }
    return arr
  }, [assets, filter, healthFilter, logoFilter, activeFilter, quoteFilter, overviewMap])

  const bybitFiltered = React.useMemo(() => {
    let options = bybit
    const q = filter.trim().toUpperCase()
    if (q) {
      options = options.filter(
        (o) => o.symbol.includes(q) || (o.name || '').toUpperCase().includes(q),
      )
    }
    if (sort === 'symbol-asc') options = [...options].sort((a, b) => a.symbol.localeCompare(b.symbol))
    if (sort === 'symbol-desc') options = [...options].sort((a, b) => b.symbol.localeCompare(a.symbol))
    if (sort === 'name-asc') options = [...options].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    if (sort === 'name-desc') options = [...options].sort((a, b) => (b.name || '').localeCompare(a.name || ''))
    return options
  }, [bybit, filter, sort])
  const existingSymbols = React.useMemo(() => new Set(assets.map(a => a.symbol)), [assets])

  return (
    <>
      <HealthOverviewModal open={ovOpen} onOpenChange={setOvOpen} />
      <div className="space-y-3">
        <ManageHeader
          title="Gerenciar cripto"
          totalAssets={assets.length}
          displayed={displayed.length}
          filter={filter}
          onChangeFilter={setFilter}
          onReload={()=> loadAll(true)}
          onOpenOverview={()=> { setOvOpen(true); setOvLoading(true); timeframeHealthOverview().then(d=>{ setOvData(d); setOvLoading(false) }).catch(()=> setOvLoading(false)) }}
          onRefreshHealth={async()=>{ await import('@/modules/crypto-assets/services/healthSnapshotService').then(m=>m.triggerRefreshHealthSnapshot()); await refreshOverview(true); toast({ title: 'Saúde', description: 'Atualizada com sucesso', soft: true }) }}
          addOpen={addOpen}
          onToggleAdd={()=> setAddOpen(v=>!v)}
          healthFilter={healthFilter}
          onChangeHealthFilter={setHealthFilter as any}
          logoFilter={logoFilter}
          onChangeLogoFilter={setLogoFilter as any}
          activeFilter={activeFilter}
          onChangeActiveFilter={setActiveFilter as any}
          quoteFilter={quoteFilter}
          onChangeQuoteFilter={setQuoteFilter as any}
        />

        <div className="grid gap-4 md:grid-cols-[1fr,480px]">
          {/* Adicionar Bybit acima da lista (full width) */}
            <AddBybitPanel
              className="md:col-span-2"
              open={addOpen}
              items={bybitFiltered}
              filter={filter}
              onChangeFilter={setFilter}
              sort={sort}
              onChangeSort={setSort}
              onAddOne={onAddOne}
              adding={addingOne}
              batchMode={batchMode}
              onToggleBatchMode={setBatchMode}
              existingSymbols={existingSymbols}
            />

          {/* Lista de ativos */}
          <AssetsInfiniteGrid
            items={displayed}
            loading={loading}
            paging={paging}
            hasMore={assets.length < total}
            onLoadMore={loadMore}
            renderItem={(a) => (
              <AssetCard
                key={a.id}
                asset={a}
                health={overviewMap[a.symbol]}
                onUploadLogo={async (asset, f)=>{ if(!f) return; const up = await uploadLogoAsset(asset.symbol, f); await updateCryptoLogo(asset.id, up.url); setAssets(prev=> prev.map(x=> x.id===asset.id? { ...x, logo_url: up.url }: x)) }}
                onOpenHealth={onOpenHealth}
                onQuickBoot={async (asset)=>{
                  try {
                    setBootOpen(true); setBootPct(0); setBootingSymbol(asset.symbol)
                    const tfs = ['60','240','720','D','W','M','30','15','5']
                    setBootTfs(tfs.map(tf => ({ tf, status: 'pending' as const })))
                    await runQuickBootstrap(asset.symbol, {
                      target: 500,
                      backBuckets: 300,
                      onStart: (_tf, idx) => { setBootTfs(prev => prev.map((t, i) => i === (idx - 1) ? { ...t, status: 'running' } : t)) },
                      onStep: (_tf, idx) => { setBootTfs(prev => prev.map((t, i) => i === (idx - 1) ? { ...t, status: 'done' } : t)) },
                      onProgress: (p) => setBootPct(p),
                    })
                    toast({ title: 'Normalização', description: `${asset.symbol}: concluído (ou máximo disponível)`, variant: 'success', soft: true })
                  } catch (e:any) { toast({ title: 'Normalização', description: e?.message || 'Falha ao normalizar', variant: 'error' }) }
                  finally { setBootOpen(false); setBootingSymbol(null); setBootTfs([]) }
                }}
                onActivate={(asset)=> { setConfirmMode('activate'); setConfirmAsset(asset); setConfirmOpen(true) }}
                onDeactivate={(asset)=> { setConfirmMode('deactivate'); setConfirmAsset(asset); setConfirmOpen(true) }}
                onEditName={(asset)=> { setEditAsset(asset); setEditOpen(true) }}
                onRemove={(asset)=> { setDelAsset(asset); setDelConfirm(''); setDelOpen(true) }}
              />
            )}
          />

        </div>
      </div>

      {/* Modal Remover */}
      <Modal open={delOpen} onOpenChange={(v)=> { if (!v) setDelOpen(false) }} className="p-6 sm:p-8" style={{ width: 520, maxWidth: 520 }}>
        <div className="mb-2 text-lg font-semibold">Remover ativo</div>
        {delAsset && (<div className="mb-3 text-sm">Ativo: <b>{delAsset.symbol}</b></div>)}
        <div className="text-sm text-foreground/70">Esta ação não pode ser desfeita. Digite o símbolo para confirmar.</div>
        <div className="mt-3">
          <label className="text-xs">Símbolo</label>
          <input className="w-full rounded-md border border-border bg-background px-2 py-2 text-sm" value={delConfirm} onChange={(e)=> setDelConfirm(e.target.value)} placeholder="EX.: SOLUSDT" />
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={()=> setDelOpen(false)}>Cancelar</Button>
          <Button variant="destructive" onClick={async()=>{ if(!delAsset) return; if (delConfirm !== delAsset.symbol) { toast({ title: 'Remover', description: 'Digite o símbolo corretamente para confirmar', variant: 'warning' }); return } setDeleting(true); try { await safeDeleteAsset(delAsset.symbol); setAssets(prev => prev.filter(x => x.id !== delAsset.id)); setDelOpen(false); toast({ title: 'Remover', description: 'Ativo removido', variant: 'success' }) } catch (e:any) { toast({ title: 'Remover', description: e?.message || 'Falha ao remover', variant: 'error' }) } finally { setDeleting(false) } }} disabled={deleting}>{deleting ? 'Removendo…' : 'Confirmar remoção'}</Button>
        </div>
      </Modal>

      {/* Modal Saúde detalhada */}
      <Modal open={healthOpen} onOpenChange={(v)=> { if (!v) setHealthOpen(false) }} className="p-6 sm:p-8" style={{ width: 1200, maxWidth: 1200 }}>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-lg font-semibold">Saúde por timeframe</div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={()=> healthAsset && onOpenHealth(healthAsset)} disabled={!healthAsset}>Atualizar</Button>
            <Button variant="secondary" onClick={onForceAllTfs} disabled={!healthAsset}>Forçar janela (9 TFs)</Button>
          </div>
        </div>
        {healthAsset && (<div className="mb-3 text-sm">Ativo: <b>{healthAsset.symbol}</b></div>)}
        {healthLoading && <div className="text-sm text-foreground/60">Carregando…</div>}
        {(!healthLoading && health) && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="p-2">TF</th>
                  <th className="p-2">Qtd</th>
                  <th className="p-2">Última vela</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Gaps</th>
                  <th className="p-2">Bucket</th>
                  <th className="p-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {health.timeframes.map((tf)=> (
                  <tr key={tf.timeframe}>
                    <td className="p-2">{tf.timeframe}</td>
                    <td className="p-2">{tf.count}</td>
                    <td className="p-2">
                      {(() => {
                        const cur = currentBuckets[tf.timeframe]
                        const ts = (cur && cur.open_time) ? cur.open_time : tf.latest
                        if (!ts) return <span className="text-foreground/60">-</span>
                        return <span>{new Date(ts).toLocaleString()}</span>
                      })()}
                    </td>
                    <td className="p-2">
                      <span className={tf.status==='ok' ? 'text-green-600' : 'text-amber-500'}>{tf.status}</span>
                      {typeof tf.stale_seconds === 'number' && tf.stale_seconds > 0 && (<span className="ml-1 text-xs text-foreground/60">(+{Math.round(tf.stale_seconds/60)}m)</span>)}
                    </td>
                    <td className="p-2">
                      {typeof (tf as any).gap_count === 'number' ? (
                        (tf as any).gap_count === 0 ? <span className="text-green-600">ok</span> : <span className="text-amber-500">gaps ({(tf as any).gap_count})</span>
                      ) : (<span className="text-foreground/60">-</span>)}
                    </td>
                    <td className="p-2">
                      {(() => {
                        const cur = currentBuckets[tf.timeframe]
                        if (!cur) return <span className="text-foreground/60">-</span>
                        if (cur && (cur.close == null || cur.slices === 0)) return <span className="text-foreground/60">aguardando</span>
                        const p = typeof cur.progress_pct === 'number' ? ` (${cur.progress_pct}% )` : ''
                        return <span>{(cur.close ?? 0).toString()}<span className="text-foreground/60">{p}</span></span>
                      })()}
                    </td>
                    <td className="p-2">
                      {tfProgress[tf.timeframe] != null ? (
                        <span className="inline-flex items-center gap-2 text-xs"><CircularProgress value={tfProgress[tf.timeframe]||0} className="text-primary" /> {tfProgress[tf.timeframe]||0}%</span>
                      ) : (
                        <Button variant="outline" className="px-2 py-1 text-xs" onClick={()=> onForceTf(healthAsset!, tf.timeframe)}>Forçar janela</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end"><Button variant="ghost" onClick={()=> setHealthOpen(false)}>Fechar</Button></div>
      </Modal>

      {/* Modal Confirmação Ativar/Inativar */}
      <Modal open={confirmOpen} onOpenChange={(v)=>{ if(!v) setConfirmOpen(false) }} className="p-6 sm:p-8" style={{ width: 560, maxWidth: 560 }}>
        <div className="mb-2 text-lg font-semibold">{confirmMode==='activate' ? 'Ativar coleta' : 'Inativar coleta'}</div>
        {confirmAsset && (<div className="mb-3 text-sm">Ativo: <b>{confirmAsset.symbol}</b></div>)}
        <div className="text-sm text-foreground/70">
          {confirmMode==='activate'
            ? 'Isto iniciará a coleta de 5m e a derivação dos timeframes maiores, acionando o runner. Verifique a estabilidade antes de ativar muitas moedas.'
            : 'Isto pausará a coleta deste ativo e cancelará tarefas pendentes/rodando na fila. Os candles existentes permanecem no banco.'}
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={()=> setConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={onConfirmAction} disabled={confirmLoading}>{confirmLoading ? 'Processando…' : (confirmMode==='activate' ? 'Confirmar ativação' : 'Confirmar inativação')}</Button>
        </div>
      </Modal>

      {/* Modal de progresso do boot */}
      <BootProgressModal
        open={bootOpen}
        percent={bootPct}
        onOpenChange={()=>{}}
        title={bootingSymbol ? ('Preparando histórico - ' + bootingSymbol) : 'Preparando histórico'}
        description={'Baixando o máximo possível de candles (até 500) por timeframe diretamente da Bybit e iniciando manutenção contínua (5m + derivação) ao final.'}
        timeframes={bootTfs}
      />

      {/* Modal Editar nome */}
      <EditAssetNameModal open={editOpen} asset={editAsset} onOpenChange={setEditOpen} onSave={onSaveEditName} />
    </>
  )
}

function AssetCard({
  asset,
  health,
  onUploadLogo,
  onOpenHealth,
  onQuickBoot,
  onActivate,
  onDeactivate,
  onEditName,
  onRemove,
}: {
  asset: CryptoAsset
  health?: TimeframeHealth
  onUploadLogo: (a: CryptoAsset, f?: File) => void
  onOpenHealth: (a: CryptoAsset) => void
  onQuickBoot: (a: CryptoAsset) => void
  onActivate: (a: CryptoAsset) => void
  onDeactivate: (a: CryptoAsset) => void
  onEditName: (a: CryptoAsset) => void
  onRemove: (a: CryptoAsset) => void
}) {
  const fileRef = React.useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = React.useState(false)
  const [progress, setProgress] = React.useState(0)

  async function onPick(f?: File){ try { setUploading(true); setProgress(0); await onUploadLogo(asset, f); setProgress(100) } finally { setUploading(false) } }

  return (
    <div className="rounded-xl border border-[#161616] bg-background/40 p-4">
      <div className="grid items-center gap-4" style={{ gridTemplateColumns: '1.3fr 1fr auto 0.3fr' }}>
        {/* Logo + nome */}
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full border border-border bg-muted" onClick={()=> fileRef.current?.click()}>
            {asset.logo_url ? (<img src={asset.logo_url} alt={asset.symbol} className="h-full w-full object-cover" />) : null}
            <span className="absolute bottom-1.5 right-1.5 rounded-full bg-background/90 p-1.5 text-foreground shadow">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            </span>
            {uploading && (<div className="absolute inset-x-0 bottom-0 h-1 bg-foreground/20"><div className="h-full bg-primary" style={{ width: `${progress}%` }} /></div>)}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e)=> onPick(e.currentTarget.files?.[0] || undefined)} />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{asset.name || asset.symbol.replace(/USDT$/,'')}</div>
            <div className="truncate text-xs text-[#434343]">{asset.symbol}</div>
          </div>
        </div>
        {/* Status */}
        <div className="min-w-0">
          <div className="text-sm font-medium">{asset.is_active ? 'Ativo' : 'Aguardando'}</div>
        </div>
        {/* Indicadores */}
        <div className="flex items-center justify-end gap-2">
          {health && (health.warnings||0) > 0 ? (
            <AlertTriangle className="h-5 w-5 text-amber-500" />
          ) : (
            <CheckCircle className="h-5 w-5 text-emerald-500" />
          )}
        </div>
        {/* Menu */}
        <div className="flex items-center justify-end">
          <DropdownMenu
            align="end"
            trigger={<button type="button" className="inline-flex items-center rounded p-1 hover:bg-muted/30"><MoreHorizontal className="h-5 w-5 text-[#434343]" /></button>}
            items={[
              { label: 'Editar nome', onSelect: () => onEditName(asset) },
              {
                label: asset.is_active ? 'Inativar coleta' : 'Ativar coleta',
                onSelect: () => (asset.is_active ? onDeactivate(asset) : onActivate(asset)),
              },
              { label: 'Forçar Normalização (boot)', onSelect: () => onQuickBoot(asset) },
              { label: 'Saúde', onSelect: () => onOpenHealth(asset) },
              { label: 'Remover', onSelect: () => onRemove(asset) },
            ]}
          />
        </div>
      </div>
    </div>
  )
}
